import type { AxiosRequestConfig } from 'axios';
import type { ValidationFieldError } from 'src/types/api';

import axios from 'axios';

import { JWT_STORAGE_KEY } from 'src/auth/context/jwt/constant';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:8080',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Queue-based refresh token state — module-level singletons
let isRefreshing = false;
let refreshQueue: Array<(token: string | null) => void> = [];

const drainRefreshQueue = (token: string | null) => {
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
};

/**
 * Request interceptor: attach Accept-Language header based on the active
 * i18next language so the backend returns localized error messages (Phase 15).
 * Import is dynamic to avoid SSR circular-dependency issues.
 */
axiosInstance.interceptors.request.use((config) => {
  try {
    // i18next is a singleton — safe to read `language` synchronously here
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const i18nModule = require('i18next');
     
    const lang = (i18nModule?.default?.language ?? i18nModule?.language ?? 'es') as string;
    config.headers['Accept-Language'] = lang;
  } catch {
    config.headers['Accept-Language'] = 'es';
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const responseData = error?.response?.data;
    const status = error?.response?.status;
    const originalRequest = error?.config as (typeof error.config) & { _retry?: boolean };

    // 401 — attempt silent token refresh before giving up
    if (status === 401 && originalRequest && !originalRequest._retry) {
      // If the refresh endpoint itself returned 401, the session is truly dead
      if (originalRequest.url?.includes('/v1/auth/refresh')) {
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem(JWT_STORAGE_KEY);
          delete (axiosInstance.defaults.headers.common as Record<string, unknown>).Authorization;
        }
        drainRefreshQueue(null);
        isRefreshing = false;
        return Promise.reject(error);
      }

      // Another refresh is already in flight — queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push((token) => {
            if (!token) return reject(error);
            originalRequest._retry = true;
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${token}`,
            };
            return resolve(axiosInstance(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Use a bare axios instance so this call is NOT intercepted again
        const refreshClient = axios.create({
          baseURL: axiosInstance.defaults.baseURL as string,
          withCredentials: true,
        });
        const { data } = await refreshClient.post<{ access_token: string }>('/v1/auth/refresh');
        const newToken = data.access_token;

        if (typeof window !== 'undefined') {
          sessionStorage.setItem(JWT_STORAGE_KEY, newToken);
        }
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${newToken}`;

        drainRefreshQueue(newToken);
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newToken}`,
        };
        return axiosInstance(originalRequest);
      } catch {
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem(JWT_STORAGE_KEY);
          delete (axiosInstance.defaults.headers.common as Record<string, unknown>).Authorization;
        }
        drainRefreshQueue(null);
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    if (status === 422 && responseData?.details) {
      // Preserve structured validation details so form handlers can call
      // parseValidationErrors() and map errors to individual fields via
      // React Hook Form's setError().
      const validationError = new Error(
        responseData?.error?.message ?? 'Error de validación'
      ) as Error & { details: ValidationFieldError[]; code: string };
      validationError.details = responseData.details as ValidationFieldError[];
      validationError.code = 'validation_error';
      return Promise.reject(validationError);
    }

    const message =
      responseData?.error?.message ??
      responseData?.message ??
      error?.message ??
      'Something went wrong!';
    console.error('Axios error:', message);

    // Preserve the original AxiosError so callers can branch on
    // `error.response.status` / `error.response.data` (e.g. the
    // `template_in_use` 409 alert in plantillas-view). Wrapping in a plain
    // Error throws away the structured body — only the validation case
    // above needs that, every other consumer needs the AxiosError.
    error.message = message;
    return Promise.reject(error);
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async <T = unknown>(
  args: string | [string, AxiosRequestConfig]
): Promise<T> => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args, {}];

    const res = await axiosInstance.get<T>(url, config);

    return res.data;
  } catch (error) {
    console.error('Fetcher failed:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    me: '/v1/users/me',
    signIn: '/v1/auth/login',
    signUp: '/v1/auth/register',
    refresh: '/v1/auth/refresh',
    logout: '/v1/auth/logout',
    verifyEmail: '/v1/auth/verify',
    resetPassword: '/v1/auth/reset-password',
    confirmReset: '/v1/auth/reset-password/confirm',
  },
  users: {
    me: '/v1/users/me',
  },
  orgs: {
    list: '/v1/orgs',
    details: (id: string) => `/v1/orgs/${id}`,
    members: (id: string) => `/v1/orgs/${id}/members`,
    assignableMembers: '/v1/organizations/members/assignable',
  },
  publications: {
    list: '/v1/publications',
    details: (id: string) => `/v1/publications/${id}`,
    create: '/v1/publications',
    update: (id: string) => `/v1/publications/${id}`,
    delete: (id: string) => `/v1/publications/${id}`,
    publish: (id: string) => `/v1/publications/${id}/publish`,
    pause: (id: string) => `/v1/publications/${id}/pause`,
    location: (id: string) => `/v1/publications/${id}/location`,
    assign: (id: string) => `/v1/publications/${id}/assign`,
    // Phase 26 — per-publication tier subscription (GET / POST / DELETE).
    tier: (id: string) => `/v1/publications/${id}/tier`,
    import: '/v1/publications/import',
    importStatus: (jobId: string) => `/v1/publications/import/${jobId}`,
  },
  locations: {
    countries: '/v1/locations/countries',
    states: (countryId: string) => `/v1/locations/countries/${countryId}/states`,
    cities: (stateId: string) => `/v1/locations/states/${stateId}/cities`,
    neighborhoods: (cityId: string) => `/v1/locations/cities/${cityId}/neighborhoods`,
  },
  media: {
    upload: '/v1/media/upload',
    setPrimary: (id: string) => `/v1/media/${id}/primary`,
    delete: (id: string) => `/v1/media/${id}`,
  },
  // Phase 26 — Billing v2 (FS-019). The old org-level subscription endpoints
  // (`/v1/payments/*`) were removed; billing is now per-org + per-publication.
  tiers: '/v1/tiers',
  billing: {
    profile: (orgId: string) => `/v1/orgs/${orgId}/billing/profile`,
    setupIntent: (orgId: string) => `/v1/orgs/${orgId}/billing/payment-method/setup-intent`,
    paymentMethod: (orgId: string) => `/v1/orgs/${orgId}/billing/payment-method`,
    invoices: (orgId: string) => `/v1/orgs/${orgId}/billing/invoices`,
    invoice: (orgId: string, invoiceId: string) => `/v1/orgs/${orgId}/billing/invoices/${invoiceId}`,
    payInvoice: (orgId: string, invoiceId: string) =>
      `/v1/orgs/${orgId}/billing/invoices/${invoiceId}/pay`,
    usage: (orgId: string) => `/v1/orgs/${orgId}/billing/usage`,
    usageHistory: (orgId: string) => `/v1/orgs/${orgId}/billing/usage/history`,
  },
  analytics: {
    agents: (orgId: string) => `/v1/orgs/${orgId}/analytics/agents`,
    publications: (orgId: string) => `/v1/orgs/${orgId}/analytics/publications`,
    summary: (orgId: string) => `/v1/orgs/${orgId}/analytics/summary`,
    eventsTimeseries: (orgId: string) => `/v1/orgs/${orgId}/analytics/events/timeseries`,
    reach: (orgId: string) => `/v1/orgs/${orgId}/analytics/reach`,
  },
  conversations: {
    list: '/v1/conversations',
    details: (id: string) => `/v1/conversations/${id}`,
    start: '/v1/conversations',
    update: (id: string) => `/v1/conversations/${id}`,
    messages: (id: string) => `/v1/conversations/${id}/messages`,
    assign: (id: string) => `/v1/conversations/${id}/assign`,
    appointments: (id: string) => `/v1/conversations/${id}/appointments`,
    appointmentUpdate: (id: string, apptId: string) =>
      `/v1/conversations/${id}/appointments/${apptId}`,
    orgAppointments: '/v1/conversations/appointments',
  },
  leads: {
    addNote: (orgId: string, leadId: string) => `/v1/orgs/${orgId}/leads/${leadId}/notes`,
    assign: (orgId: string, leadId: string) => `/v1/orgs/${orgId}/leads/${leadId}/assign`,
    detail: (orgId: string, leadId: string) => `/v1/orgs/${orgId}/leads/${leadId}`,
    list: (orgId: string) => `/v1/orgs/${orgId}/leads`,
    updateStage: (orgId: string, leadId: string) => `/v1/orgs/${orgId}/leads/${leadId}`,
  },
  favorites: {
    list: '/v1/favorites',
    count: '/v1/favorites/count',
    add: (publicationId: string) => `/v1/favorites/${publicationId}`,
    remove: (publicationId: string) => `/v1/favorites/${publicationId}`,
    update: (publicationId: string) => `/v1/favorites/${publicationId}`,
    share: '/v1/favorites/share',
  },
  // Stub endpoints for Minimals CC demo components (replaced in Phase 2+)
  post: {
    list: '/v1/posts',
    details: '/v1/posts/details',
    latest: '/v1/posts/latest',
    search: '/v1/posts/search',
  },
  product: {
    list: '/v1/products',
    details: '/v1/products/details',
    search: '/v1/products/search',
  },
  notifications: {
    list: '/v1/notifications',
    unreadCount: '/v1/notifications/unread-count',
    markRead: (id: string) => `/v1/notifications/${id}/read`,
    markAllRead: '/v1/notifications/read-all',
    deleteAll: '/v1/notifications',
  },
  deviceTokens: '/v1/users/me/device-tokens',
  deviceTokenRegister: '/v1/users/me/device-token',
  calendar: '/v1/calendar',
  chat: '/v1/chat',
  kanban: '/v1/kanban',
  mail: {
    labels: '/v1/mail/labels',
    list: '/v1/mail',
    details: '/v1/mail/details',
  },
};
